<script setup lang="ts">
import {
  BeamShapingGroupRectangularDto,
  NumericValueDto,
  TaskTypeDescription,
} from '@/api/generator';
import VeeEnumerationValue from '@/components/forms/dataTypes/VeeEnumerationValue.vue';
import VeeNumericValueField from '@/components/forms/dataTypes/VeeNumericValueField.vue';
import Select from '@/components/forms/Select/Select.vue';
import { SelectItemType } from '@/components/forms/types.ts';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { injectStrict } from '@/utils/injectStrict.ts';
import { toDisplayValue } from '@/utils/numberUtils';
import { I18nKey } from '@/utils/typings.ts';
import { bccBeamShapingRectangularFieldSizeXOptionsInjectionKey } from '@/views/tasks/components/beamShaping/utils.ts';
import { vElementSize } from '@vueuse/components';
import { useField, useFieldError, useFieldValue } from 'vee-validate';
import { computed, type ComputedRef, type Ref, ref, toRef, watch, watchEffect } from 'vue';

type BeamShapingGroupRectangularProps = {
  name: string;
  taskType: TaskTypeDescription;
  disabled?: boolean;
};

const props = withDefaults(defineProps<BeamShapingGroupRectangularProps>(), {
  disabled: false,
});

const emits = defineEmits<{
  blur: [event?: FocusEvent];
}>();

const mode = useFormMode();

const { value: item, handleBlur } = useField<BeamShapingGroupRectangularDto>(
  toRef(() => props.name),
);

function onBlur(event?: FocusEvent) {
  handleBlur(event);
  emits('blur', event);
}

const beamConstancyFieldSizeXOptions = injectStrict<ComputedRef<SelectItemType<number>[]>>(
  bccBeamShapingRectangularFieldSizeXOptionsInjectionKey,
);

// Symmetry
const radiationFieldSymmetry = useFieldValue<string>(
  toRef(() => `${props.name}.radiationFieldSymmetry.value.name`),
);

const isSymmetrical = () =>
  radiationFieldSymmetry.value === ('EnumerationOptionLabelEnum.Symmetrical' as I18nKey);

const isAsymmetrical = () =>
  radiationFieldSymmetry.value === ('EnumerationOptionLabelEnum.Asymmetrical' as I18nKey);

// Field size
const selectedBeamConstancyFieldSizeX = ref<number | null>();

// Field edges
const { setValue: setRadiationFieldEdgeX1, errorMessage: radiationFieldEdgeX1Error } = useField<
  number | undefined
>(toRef(() => `${props.name}.radiationFieldEdgeX1.value`));

const { setValue: setRadiationFieldEdgeY1, errorMessage: radiationFieldEdgeY1Error } = useField<
  number | undefined
>(toRef(() => `${props.name}.radiationFieldEdgeY1.value`));

const { setValue: setRadiationFieldEdgeX2, errorMessage: radiationFieldEdgeX2Error } = useField<
  number | undefined
>(toRef(() => `${props.name}.radiationFieldEdgeX2.value`));

const { setValue: setRadiationFieldEdgeY2, errorMessage: radiationFieldEdgeY2Error } = useField<
  number | undefined
>(toRef(() => `${props.name}.radiationFieldEdgeY2.value`));

const hasErrors = computed(() => {
  return (
    radiationFieldSizeXError.value ||
    radiationFieldSizeYError.value ||
    radiationFieldEdgeX1Error.value ||
    radiationFieldEdgeX2Error.value ||
    radiationFieldEdgeY1Error.value ||
    radiationFieldEdgeY2Error.value
  );
});

const { setValue: setRadiationFieldSizeX } = useField<number | undefined>(
  toRef(() => `${props.name}.radiationFieldSizeX.value`),
);

const radiationFieldSizeXError = useFieldError(
  toRef(() => `${props.name}.radiationFieldSizeX.value`),
);

const { setValue: setRadiationFieldSizeY } = useField<number | undefined>(
  toRef(() => `${props.name}.radiationFieldSizeY.value`),
);

const radiationFieldSizeYError = useFieldError(
  toRef(() => `${props.name}.radiationFieldSizeY.value`),
);

function computeFieldSize(edge: 'X1' | 'X2' | 'Y1' | 'Y2', value: NumericValueDto | undefined) {
  if (!isAsymmetrical() || typeof value?.value !== 'number') return;

  const computeSize = (
    oppositeEdge: NumericValueDto | undefined,
    setSize: (value: number | undefined) => void,
    errorX: Ref<string | undefined>,
    errorY: Ref<string | undefined>,
  ) => {
    if (
      oppositeEdge?.value === undefined ||
      oppositeEdge?.value === null ||
      value.value === undefined ||
      value.value === null
    )
      return;
    setSize(errorX.value || errorY.value ? undefined : Math.abs(oppositeEdge.value - value.value));
  };

  switch (edge) {
    case 'X1':
      computeSize(
        item.value.radiationFieldEdgeX2,
        setRadiationFieldSizeX,
        radiationFieldEdgeX1Error,
        radiationFieldEdgeX2Error,
      );
      break;
    case 'X2':
      computeSize(
        item.value.radiationFieldEdgeX1,
        setRadiationFieldSizeX,
        radiationFieldEdgeX1Error,
        radiationFieldEdgeX2Error,
      );
      break;
    case 'Y1':
      computeSize(
        item.value.radiationFieldEdgeY2,
        setRadiationFieldSizeY,
        radiationFieldEdgeY1Error,
        radiationFieldEdgeY2Error,
      );
      break;
    case 'Y2':
      computeSize(
        item.value.radiationFieldEdgeY1,
        setRadiationFieldSizeY,
        radiationFieldEdgeY1Error,
        radiationFieldEdgeY2Error,
      );
      break;
  }
}

function setXFieldEdges(value: NumericValueDto | undefined) {
  if (typeof value?.value === 'number' && !radiationFieldSizeXError.value) {
    setRadiationFieldEdgeX1(-1 * (value.value / 2));
    setRadiationFieldEdgeX2(value.value / 2);
  } else {
    setRadiationFieldEdgeX1(undefined);
    setRadiationFieldEdgeX2(undefined);
  }
}

function setYFieldEdges(value: NumericValueDto | undefined) {
  if (typeof value?.value === 'number' && !radiationFieldSizeYError.value) {
    setRadiationFieldEdgeY1(-1 * (value.value / 2), false);
    setRadiationFieldEdgeY2(value.value / 2, false);
  } else {
    setRadiationFieldEdgeY1(undefined);
    setRadiationFieldEdgeY2(undefined);
  }
}

function computeFieldEdges(dimension: 'X' | 'Y', value: NumericValueDto | undefined) {
  if (!isSymmetrical()) {
    return;
  }

  if (dimension === 'X') {
    setXFieldEdges(value);
  } else {
    setYFieldEdges(value);
  }
}

watchEffect(() => {
  function handleBeamConstancyCheck() {
    if (!selectedBeamConstancyFieldSizeX.value && !item.value.radiationFieldSizeX?.value) {
      return;
    }

    if (selectedBeamConstancyFieldSizeX.value) {
      setRadiationFieldSizeX(selectedBeamConstancyFieldSizeX.value);
      setRadiationFieldSizeY(selectedBeamConstancyFieldSizeX.value);
      computeFieldEdges('X', item.value.radiationFieldSizeX);
      computeFieldEdges('Y', item.value.radiationFieldSizeX);
      return;
    }

    selectedBeamConstancyFieldSizeX.value = item.value.radiationFieldSizeX?.value;
    computeFieldEdges('X', item.value.radiationFieldSizeX);
  }

  function handleManualTaskAsymmetrical() {
    computeFieldSize('X1', item.value.radiationFieldEdgeX1);
    computeFieldSize('X2', item.value.radiationFieldEdgeX2);
    computeFieldSize('Y1', item.value.radiationFieldEdgeY1);
    computeFieldSize('Y2', item.value.radiationFieldEdgeY2);
  }

  function handleManualTaskSymmetrical() {
    if (typeof item.value.radiationFieldSizeX?.value === 'number') {
      setRadiationFieldSizeX(item.value.radiationFieldSizeX?.value);
      computeFieldEdges('X', item.value.radiationFieldSizeX);
    }

    if (typeof item.value.radiationFieldSizeY?.value === 'number') {
      setRadiationFieldSizeY(item.value.radiationFieldSizeY?.value);
      computeFieldEdges('Y', item.value.radiationFieldSizeY);
    }
  }

  if (props.taskType === 'BeamConstancyCheck') {
    handleBeamConstancyCheck();
  }

  if (props.taskType === 'ManualTask' && item.value) {
    if (radiationFieldSymmetry.value === ('EnumerationOptionLabelEnum.Asymmetrical' as I18nKey)) {
      handleManualTaskAsymmetrical();
    }
    if (radiationFieldSymmetry.value === ('EnumerationOptionLabelEnum.Symmetrical' as I18nKey)) {
      handleManualTaskSymmetrical();
    }
  }
});

const sizeElHeightRef = ref('initial');
function onResize({ height }: { height: number }) {
  sizeElHeightRef.value = `${height / 2 + 8}px`;
}

watch(
  () => hasErrors.value,
  (hasErrorsNew) => {
    if (!hasErrorsNew) {
      sizeElHeightRef.value = 'initial';
    }
  },
);

const getNumberWithDecimalPlaces = (numericValueDto: NumericValueDto | undefined): string => {
  return toDisplayValue(numericValueDto?.value, {
    valueType: numericValueDto?.valueType,
    fractionDigits: numericValueDto?.decimalPlaces,
    unitAbbreviation: numericValueDto?.unitAbbreviation,
  });
};
</script>

<template>
  <td class="symmetry">
    <VeeEnumerationValue
      :disabled="true"
      :name="`${name}.radiationFieldSymmetry`"
      :items="
        item?.radiationFieldSymmetry?.options?.map((option) => {
          return {
            title: `${$t(option.name as I18nKey)}`,
            value: option,
            props: {
              disabled: option.name !== ('EnumerationOptionLabelEnum.Symmetrical' as I18nKey),
            },
          };
        })
      "
      :mode="'details'"
      @blur="onBlur"
    />
  </td>
  <td v-element-size="onResize" class="size">
    <Select
      v-if="taskType === 'BeamConstancyCheck' && mode !== 'details'"
      v-model="selectedBeamConstancyFieldSizeX"
      class="mb-4"
      :name="`${name}.radiationFieldSizeXOptions`"
      :items="beamConstancyFieldSizeXOptions"
      :mode="mode"
      :prefix="$t(item.radiationFieldSizeX?.geometry?.name as I18nKey)"
      :suffix="item.radiationFieldSizeX?.unitAbbreviation"
      @update:modelValue="onBlur"
    />
    <VeeNumericValueField
      v-else-if="mode !== 'details'"
      class="mb-4 align-center justify-end"
      :name="`${name}.radiationFieldSizeX`"
      :mode="mode"
      :disabled="radiationFieldSymmetry !== ('EnumerationOptionLabelEnum.Symmetrical' as I18nKey)"
      :prefix="$t(item.radiationFieldSizeX?.geometry?.name as I18nKey)"
      @blur="
        (event: FocusEvent) => {
          const target = event.target as HTMLInputElement;
          if (target.value === '') {
            setRadiationFieldSizeX(undefined);
          }
          onBlur(event);
        }
      "
      @update:modelValue="computeFieldEdges('X', $event)"
    />
    <div v-else class="mb-4 d-flex align-center justify-end">
      X:
      {{ getNumberWithDecimalPlaces(item.radiationFieldSizeX) }}
    </div>

    <VeeNumericValueField
      v-if="mode !== 'details' && taskType !== 'BeamConstancyCheck'"
      class="align-center justify-end"
      :name="`${name}.radiationFieldSizeY`"
      :mode="mode"
      :disabled="radiationFieldSymmetry !== ('EnumerationOptionLabelEnum.Symmetrical' as I18nKey)"
      :prefix="$t(item.radiationFieldSizeY?.geometry?.name as I18nKey)"
      @blur="
        (event: FocusEvent) => {
          const target = event.target as HTMLInputElement;
          if (target.value === '') {
            setRadiationFieldSizeY(undefined);
          }
          onBlur(event);
        }
      "
      @update:modelValue="computeFieldEdges('Y', $event)"
    />
    <div v-else class="d-flex align-center justify-end">
      Y:
      {{ getNumberWithDecimalPlaces(item.radiationFieldSizeY) }}
    </div>
  </td>
  <td class="edge">
    <v-table>
      <tbody>
        <tr class="table-row">
          <td>
            <div class="d-flex align-center justify-end fill-height">
              {{ $t(item.radiationFieldEdgeX1?.geometry?.name as I18nKey) }}:
              {{ getNumberWithDecimalPlaces(item.radiationFieldEdgeX1) }}
            </div>
          </td>
          <td>
            <div class="d-flex align-center justify-end fill-height">
              {{ $t(item.radiationFieldEdgeX2?.geometry?.name as I18nKey) }}:
              {{ getNumberWithDecimalPlaces(item.radiationFieldEdgeX2) }}
            </div>
          </td>
        </tr>
        <tr class="table-row">
          <td>
            <div class="d-flex align-center justify-end fill-height">
              {{ $t(item.radiationFieldEdgeY1?.geometry?.name as I18nKey) }}:
              {{ getNumberWithDecimalPlaces(item.radiationFieldEdgeY1) }}
            </div>
          </td>
          <td>
            <div class="d-flex align-center justify-end fill-height">
              {{ $t(item.radiationFieldEdgeY2?.geometry?.name as I18nKey) }}:
              {{ getNumberWithDecimalPlaces(item.radiationFieldEdgeY2) }}
            </div>
          </td>
        </tr>
      </tbody>
    </v-table>
  </td>
</template>

<style lang="scss" scoped>
.table-row {
  justify-content: space-around;
}
.v-table {
  table {
    tbody {
      tr {
        td {
          &.symmetry {
            width: 20% !important;
          }
          &.size {
            width: 20% !important;
            :deep(.v-select) {
              .v-field__input {
                justify-content: end;
              }
            }
          }
          &.edge {
            padding: 0 !important;
            width: auto !important;
            :deep(.v-table) {
              table {
                table-layout: fixed;
                tbody {
                  tr {
                    background-color: transparent;
                    td {
                      background-color: transparent !important;
                      height: v-bind(sizeElHeightRef);

                      &:first-child {
                        border-left: none;
                      }
                    }
                  }
                }
              }
            }
          }
          &.remove {
            width: 56px !important;
            padding-left: 8px !important;
          }
        }
      }
    }
  }
}
</style>
